import { Match } from 'effect';
import React from 'react';
import styled, { css } from 'styled-components';
import { ColorUtils, GetColor } from 'venn-ui-kit';
import circleSvg from './circle_4px.svg';

export type BarFillType = 'striped' | 'solid' | 'diagonal striped' | 'spaced circles';
export const rangeBarBackground = ({ fillType, color }: { fillType: BarFillType; color: string }) => css`
  ${Match.value(fillType).pipe(
    Match.when(
      'diagonal striped',
      () => css`
        background: repeating-linear-gradient(-45deg, ${color}, ${color} 2px, transparent 2px, transparent 4px);
      `,
    ),
    Match.when(
      'striped',
      () => css`
        background: repeating-linear-gradient(to right, ${color} 0, ${color} 4px, transparent 4px, transparent 8px);
      `,
    ),
    Match.when(
      'solid',
      () => css`
        background: ${color};
      `,
    ),
    Match.when(
      'spaced circles',
      () => css`
        background-image: url(${circleSvg});
        background-size: 7px 4px; /* 4px circle, 3px space */
        background-repeat: repeat-x;
        color: ${color};
        //
      `,
    ),
    Match.exhaustive,
  )}
`;
export const overlapColor = ColorUtils.opacifyFrom(GetColor.DarkBlue, 0.15);
type OverlapBarProps = {
  percentageWidth: number;
  percentageStart: number;
};

const RangeBar = styled.div<{
  widthPercentage: number;
  leftMarginPercentage: number;
  fillType: BarFillType;
  color: string;
  height: number;
}>`
  ${rangeBarBackground}
  ${({ fillType }) =>
    Match.value(fillType).pipe(
      Match.when(
        'spaced circles',
        () => css`
          background-position: 2px 0; /* add a 2px margin to the left */
        `,
      ),
      Match.orElse(() => css``),
    )}

  padding: 0;
  position: absolute;

  ${({ widthPercentage, leftMarginPercentage, height }) => css`
    height: ${height}px;
    top: calc(50% - ${Math.floor(height / 2)}px);
    width: ${widthPercentage === 0 ? '1px' : `${widthPercentage * 100}%`};
    margin-left: calc(${leftMarginPercentage * 100 ?? 0}% - ${widthPercentage === 0 ? 1 : 0}px);
  `}
`;

const RangeBarWithGap = styled(RangeBar)<{ hasGap: boolean }>`
  /* Use offsetIndex for spacing: shift margin & shrink width */
  ${({ widthPercentage, leftMarginPercentage, hasGap }) => {
    const gap = hasGap ? 1 : 0;
    return css`
      width: ${`${widthPercentage === 0 ? '1px' : `calc(${widthPercentage * 100}% - ${gap}px)`}`};
      margin-left: calc(${leftMarginPercentage * 100}% + ${gap}px - ${widthPercentage === 0 ? gap + 1 : 0}px);
    `;
  }}
`;
export const RangeCellRendererPrimitives = {
  OverlapBar: (overlap: OverlapBarProps) => {
    return overlap.percentageWidth > 0 ? (
      <OverlapBar widthPercentage={overlap.percentageWidth} leftMarginPercentage={overlap.percentageStart} />
    ) : null;
  },
  RangeBar,
  RangeBarWithGap,
};

const OverlapBar = styled.div<{
  widthPercentage: number;
  leftMarginPercentage: number;
}>`
  background-color: ${overlapColor};
  height: 100%;
  width: ${({ widthPercentage }) => widthPercentage * 100}%;
  margin-left: ${({ leftMarginPercentage }) => leftMarginPercentage * 100 ?? 0}%;
  padding: 0;
  top: 0;
  position: absolute;
`;
